// A seed's waveform: the shape of its master, small enough to ride every feed
// card. WAVEFORM_BUCKETS peaks, each the loudest sample in its slice of the
// track (all channels), scaled so the loudest slice is 255 — SoundCloud's
// normalised look, where a quiet master still draws a readable shape.
//
// Stored and sent as base64 of the raw bytes (344 characters). Pure: the
// extension computes it at upload from the master it just mixed, the API
// validates it, and the web app computes it for older seeds from the preview
// it can already decode.

import type { DecodedAudio } from "./master-mix.js";

export const WAVEFORM_BUCKETS = 256;

/** Peaks of interleaved samples, normalised to 0–255. All zero for silence. */
export function peaksOf(audio: Pick<DecodedAudio, "samples" | "channels" | "frames">, buckets = WAVEFORM_BUCKETS): Uint8Array {
  const out = new Float32Array(buckets);
  const { samples, channels, frames } = audio;
  if (frames > 0 && channels > 0) {
    for (let b = 0; b < buckets; b++) {
      const from = Math.floor((b * frames) / buckets);
      const to = Math.max(from + 1, Math.floor(((b + 1) * frames) / buckets));
      let peak = 0;
      for (let f = from; f < to && f < frames; f++) {
        for (let ch = 0; ch < channels; ch++) {
          const v = Math.abs(samples[f * channels + ch] ?? 0);
          if (v > peak) peak = v;
        }
      }
      out[b] = peak;
    }
  }
  let max = 0;
  for (const v of out) if (v > max) max = v;
  const bytes = new Uint8Array(buckets);
  if (max > 0) for (let b = 0; b < buckets; b++) bytes[b] = Math.round((out[b]! / max) * 255);
  return bytes;
}

// Base64 by hand: the Ableton host's Node lacks some web globals (it has no
// Headers or Request), so btoa / atob are not something to lean on there.
const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";

export function encodeWaveform(peaks: Uint8Array): string {
  let out = "";
  for (let i = 0; i < peaks.length; i += 3) {
    const a = peaks[i]!;
    const b = peaks[i + 1];
    const c = peaks[i + 2];
    out += ALPHABET[a >> 2]!;
    out += ALPHABET[((a & 3) << 4) | ((b ?? 0) >> 4)]!;
    out += b === undefined ? "=" : ALPHABET[((b & 15) << 2) | ((c ?? 0) >> 6)]!;
    out += c === undefined ? "=" : ALPHABET[c & 63]!;
  }
  return out;
}

/** The peaks a stored waveform holds — null for anything that is not one. */
export function decodeWaveform(value: string | null | undefined): Uint8Array | null {
  if (!value || value.length % 4 !== 0) return null;
  const bytes: number[] = [];
  for (let i = 0; i < value.length; i += 4) {
    const quad = [0, 1, 2, 3].map((k) => (value[i + k] === "=" ? -2 : ALPHABET.indexOf(value[i + k]!)));
    if (quad.some((v) => v === -1) || quad[0]! < 0 || quad[1]! < 0) return null;
    const [w, x, y, z] = quad as [number, number, number, number];
    bytes.push((w << 2) | (x >> 4));
    if (y >= 0) bytes.push(((x & 15) << 4) | (y >> 2));
    if (z >= 0) {
      if (y < 0) return null;
      bytes.push(((y & 3) << 6) | z);
    }
  }
  return bytes.length === WAVEFORM_BUCKETS ? Uint8Array.from(bytes) : null;
}

export function isWaveform(value: unknown): value is string {
  return typeof value === "string" && decodeWaveform(value) !== null;
}

/** A plausible track length: a whole number of milliseconds, up to a day. */
export function isDurationMs(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value > 0 && value <= 24 * 60 * 60 * 1000;
}
